// ============================================================================
// Notifications Panel Component
// ============================================================================
// Modern dropdown panel showing recent notifications with grouping,
// relative timestamps, and smooth animations
// ============================================================================

import { getNotifications, getUnreadCount, markAsRead, markAllAsRead } from 'shared/data/notifications-api.js';
import { escapeHtml, getInitials } from 'shared/utils/dom-helpers.js';
import { formatDate } from 'shared/utils/shared.js';
import { getMyReminders, deleteReminder, getRemindersCount } from 'shared/data/reminders-api.js';
import { withButtonLoading } from 'shared/utils/button-loading.js';
import { showToast } from 'shared/toast/toast.js';
import { EventManager } from 'shared/utils/event-manager.js';
import {
    getTypeConfig,
    getNotificationLabel,
    getNavigationUrl,
    getActorName,
    stripMentionMarkdown,
    getNotificationDetail,
} from 'shared/notifications/notification-registry.js';

// Group window: consecutive notifications of the same type within 2 minutes
const GROUP_WINDOW_MS = 2 * 60 * 1000;



// ---------------------------------------------------------------------------
// Relative time helper
// ---------------------------------------------------------------------------
function getRelativeTime(dateStr) {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMs = now - then;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay === 1) return 'Yesterday';
    if (diffDay <= 6) return `${diffDay}d ago`;
    return formatDate(new Date(dateStr));
}

// ---------------------------------------------------------------------------
// Grouping logic
// ---------------------------------------------------------------------------
function buildGroups(notifications) {
    if (!notifications.length) return [];

    const groups = [];
    let current = null;

    for (const n of notifications) {
        const ts = new Date(n.created_at).getTime();

        if (
            current &&
            current.type === n.type &&
            current.lastTs - ts <= GROUP_WINDOW_MS
        ) {
            current.items.push(n);
            current.lastTs = ts;
        } else {
            current = { type: n.type, items: [n], lastTs: ts };
            groups.push(current);
        }
    }

    return groups;
}

// ---------------------------------------------------------------------------
// NotificationsPanel class
// ---------------------------------------------------------------------------
class NotificationsPanel {
    constructor() {
        this.isOpen = false;
        this.notifications = [];
        this.unreadCount = 0;
        this.container = null;
        this.panelEl = null;
        this.bellEl = null;
        this.boundHandleClickOutside = this.handleClickOutside.bind(this);
        this.pageSize = 20;
        this.hasMore = false;
        this._listDelegated = false;
        this._expandedGroups = new Set();
        this._timestampInterval = null;
        this._autoReadTimer = null;
        // Last count painted onto the bell. null until the first read resolves.
        this._renderedCount = null;

        // Reminders view state
        this._activeView = 'notifications'; // 'notifications' | 'reminders'
        this._reminders = [];
        this._remindersCount = 0;
        this._remindersCountLoaded = false;
        this._remindersLoaded = false;
        this._remindersDelegated = false;

        // One EventManager owns every DOM listener, timer, and window event the
        // panel attaches. destroy() calls events.dispose().
        this.events = new EventManager('NotificationsPanel');
    }

    // -----------------------------------------------------------------------
    // Lifecycle
    // -----------------------------------------------------------------------

    async init(container) {
        this.container = container;
        this.render();
        this.attachEventListeners();
        await this.refreshUnreadCount();
        // Reminders count deferred to first panel open to avoid startup network call

        this.events.on(window, 'notification-received', (e) => this._handleNewNotification(e.detail));

        this.events.on(window, 'reminder-created', () => {
            this._remindersLoaded = false; // Invalidate cache
            this._refreshRemindersCount();
            if (this.isOpen && this._activeView === 'reminders') {
                this._loadReminders(true);
            }
        });

        this.maybeAutoOpenOnLogin();
    }

    render() {
        if (!this.container) return;

        this.container.innerHTML = `
            <button class="notifications-bell" id="notifications-bell" title="Notifications">
                <i class="fas fa-bell"></i>
                <span class="notifications-bell__badge ${this.unreadCount === 0 ? 'notifications-bell__badge--hidden' : ''}" id="notifications-badge">${this.unreadCount > 9 ? '9+' : this.unreadCount}</span>
            </button>
            <div class="notifications-panel" id="notifications-panel">
                <div class="notifications-panel__accent"></div>
                <div class="notifications-panel__header">
                    <div class="notifications-panel__tabs">
                        <button class="notifications-panel__tab notifications-panel__tab--active" data-tab="notifications">
                            <i class="fas fa-bell"></i>
                            <span>Notifications</span>
                            <span class="notifications-panel__tab-badge ${this.unreadCount === 0 ? 'notifications-panel__tab-badge--hidden' : ''}" id="notifications-tab-badge">${this.unreadCount > 9 ? '9+' : this.unreadCount}</span>
                        </button>
                        <button class="notifications-panel__tab" data-tab="reminders">
                            <i class="fas fa-clock"></i>
                            <span>Reminders</span>
                            <span class="notifications-panel__tab-badge ${this._remindersCount === 0 ? 'notifications-panel__tab-badge--hidden' : ''}" id="reminders-tab-badge">${this._remindersCount}</span>
                        </button>
                    </div>
                    <div class="notifications-panel__header-actions" id="notifications-header-actions">
                        <div data-view="notifications">
                            <button class="notifications-panel__header-btn" id="mark-all-read" title="Mark all as read">
                                <i class="fas fa-check-double"></i>
                            </button>
                            <button class="notifications-panel__header-btn" id="notifications-settings" title="Notification settings">
                                <i class="fas fa-cog"></i>
                            </button>
                        </div>
                        <div data-view="reminders" style="display:none">
                            <button class="notifications-panel__header-btn notifications-panel__header-btn--add" id="reminder-add-btn" title="New Reminder">
                                <i class="fas fa-plus"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="notifications-panel__list" id="notifications-list">
                    <div class="notifications-panel__loading">
                        <i class="fas fa-spinner fa-spin"></i>
                    </div>
                </div>
                <div class="notifications-panel__reminders" id="notifications-reminders" style="display:none">
                    <div class="notifications-panel__loading">
                        <i class="fas fa-spinner fa-spin"></i>
                    </div>
                </div>
                <div class="notifications-panel__footer" id="notifications-footer">
                    <i class="fas fa-cog"></i>
                    <span>Manage notifications</span>
                </div>
            </div>
        `;

        this.bellEl = this.container.querySelector('#notifications-bell');
        this.panelEl = this.container.querySelector('#notifications-panel');
    }

    attachEventListeners() {
        if (!this.bellEl) return;

        this.events.on(this.bellEl, 'click', (e) => {
            e.stopPropagation();
            this.toggle();
        });

        const markAllBtn = this.container.querySelector('#mark-all-read');
        if (markAllBtn) {
            this.events.on(markAllBtn, 'click', (e) => {
                e.stopPropagation();
                withButtonLoading(
                    e.currentTarget,
                    () => this.handleMarkAllRead(),
                    {
                        successMessage: 'All marked read',
                        errorMessage: 'Failed to mark as read'
                    }
                );
            });
        }

        const settingsBtn = this.container.querySelector('#notifications-settings');
        if (settingsBtn) {
            this.events.on(settingsBtn, 'click', (e) => {
                e.stopPropagation();
                this.close();
                window.location.hash = '#/settings?tab=notifications';
            });
        }

        const footerEl = this.container.querySelector('#notifications-footer');
        if (footerEl) {
            this.events.on(footerEl, 'click', (e) => {
                e.stopPropagation();
                this.close();
                window.location.hash = '#/settings?tab=notifications';
            });
        }

        const addReminderBtn = this.container.querySelector('#reminder-add-btn');
        if (addReminderBtn) {
            this.events.on(addReminderBtn, 'click', async (e) => {
                e.stopPropagation();
                this.close();
                const { showReminderModal } = await import('shared/components/reminders/reminder-modal.js');
                showReminderModal();
            });
        }

        // Tab switching
        const tabs = this.container.querySelectorAll('.notifications-panel__tab');
        tabs.forEach(tab => {
            this.events.on(tab, 'click', (e) => {
                e.stopPropagation();
                const view = tab.dataset.tab;
                if (view === this._activeView) return;
                this._switchView(view);
            });
        });
    }

    // -----------------------------------------------------------------------
    // Open / Close / Toggle
    // -----------------------------------------------------------------------

    async toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            await this.open();
        }
    }

    async open({ autoRead = true } = {}) {
        this.isOpen = true;
        this.panelEl?.classList.add('notifications-panel--open');

        // Lazy-load reminders count on first open
        if (!this._remindersCountLoaded) {
            this._remindersCountLoaded = true;
            this._refreshRemindersCount();
        }

        if (this._activeView === 'notifications') {
            await this.loadNotifications();
            this._startTimestampUpdater();
            if (autoRead) this._scheduleAutoRead();
        } else {
            await this._loadReminders();
        }

        this.events.setTimeout(() => {
            this.events.on(document, 'click', this.boundHandleClickOutside);
        }, 10);
    }

    close() {
        this.isOpen = false;
        this.panelEl?.classList.remove('notifications-panel--open');
        // off() is a no-op if the listener wasn't attached yet (timer race)
        this.events.off(document, 'click', this.boundHandleClickOutside);
        this._stopTimestampUpdater();
        this._clearAutoRead();
    }

    /**
     * Once per browser session, shortly after the dashboard has loaded, auto-open
     * the panel if there are unread notifications - a "here's what you missed"
     * summary on login. Does NOT auto-mark-read (the user has not engaged yet).
     */
    maybeAutoOpenOnLogin() {
        if (sessionStorage.getItem('aerium-login-summary-shown')) return;
        sessionStorage.setItem('aerium-login-summary-shown', '1');
        this.events.setTimeout(async () => {
            await this.refreshUnreadCount();
            if (this.unreadCount > 0 && !this.isOpen) {
                this.open({ autoRead: false });
            }
        }, 1800);
    }

    /**
     * When the user opens the panel, mark everything read after a short dwell -
     * they have seen it. Cancelled if the panel closes first.
     */
    _scheduleAutoRead() {
        this._clearAutoRead();
        if (this.unreadCount === 0) return;
        this._autoReadTimer = this.events.setTimeout(() => {
            this._autoReadTimer = null;
            if (this.isOpen && this._activeView === 'notifications' && this.unreadCount > 0) {
                this.handleMarkAllRead().catch(err =>
                    console.error('[NotificationsPanel] auto mark-all-read failed:', err));
            }
        }, 1000);
    }

    _clearAutoRead() {
        if (this._autoReadTimer) {
            this.events.clearTimeout(this._autoReadTimer);
            this._autoReadTimer = null;
        }
    }

    handleClickOutside(e) {
        if (!this.container?.contains(e.target)) {
            this.close();
        }
    }

    // -----------------------------------------------------------------------
    // Data loading
    // -----------------------------------------------------------------------

    async loadNotifications() {
        const listEl = this.container?.querySelector('#notifications-list');
        if (!listEl) return;

        this.notifications = [];
        this.hasMore = false;
        this._expandedGroups.clear();

        const { data, error } = await getNotifications({ limit: this.pageSize });

        if (error) {
            listEl.innerHTML = `
                <div class="notifications-panel__empty">
                    <i class="fas fa-exclamation-circle"></i>
                    <span class="notifications-panel__empty-title">Something went wrong</span>
                    <span class="notifications-panel__empty-subtitle">Failed to load notifications</span>
                </div>
            `;
            return;
        }

        this.notifications = data;
        this.hasMore = data.length >= this.pageSize;

        if (data.length === 0) {
            listEl.innerHTML = `
                <div class="notifications-panel__empty">
                    <i class="fas fa-bell-slash"></i>
                    <span class="notifications-panel__empty-title">You're all caught up!</span>
                    <span class="notifications-panel__empty-subtitle">New notifications will appear here</span>
                </div>
            `;
            return;
        }

        this._renderNotificationsList(listEl);
    }

    async loadMore() {
        const listEl = this.container?.querySelector('#notifications-list');
        if (!listEl || !this.hasMore) return;

        const loadMoreBtn = listEl.querySelector('.notifications-panel__load-more');
        if (loadMoreBtn) {
            loadMoreBtn.disabled = true;
            loadMoreBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Loading...';
        }

        const { data, error } = await getNotifications({
            limit: this.pageSize,
            offset: this.notifications.length
        });

        if (error || !data) {
            if (loadMoreBtn) {
                loadMoreBtn.disabled = false;
                loadMoreBtn.innerHTML = 'Load more';
            }
            return;
        }

        this.notifications = this.notifications.concat(data);
        this.hasMore = data.length >= this.pageSize;
        this._renderNotificationsList(listEl);
    }

    // -----------------------------------------------------------------------
    // Handle new notification from realtime
    // -----------------------------------------------------------------------

    _handleNewNotification(notification) {
        this.refreshUnreadCount();

        if (!this.isOpen || this._activeView !== 'notifications' || !notification) return;

        // Use the realtime payload directly - no extra fetch needed.
        if (!this.notifications.length || notification.id !== this.notifications[0].id) {
            this.notifications.unshift(notification);
            const listEl = this.container?.querySelector('#notifications-list');
            if (listEl) this._renderNotificationsList(listEl);
        }
    }

    // -----------------------------------------------------------------------
    // Rendering
    // -----------------------------------------------------------------------

    _renderNotificationsList(listEl) {
        const groups = buildGroups(this.notifications);
        let html = '';

        for (let gi = 0; gi < groups.length; gi++) {
            const group = groups[gi];
            if (group.items.length > 1) {
                html += this._renderGroup(group, gi);
            } else {
                html += this._renderNotification(group.items[0]);
            }
        }

        if (this.hasMore) {
            html += `
                <div class="notifications-panel__load-more-wrapper">
                    <button class="notifications-panel__load-more" type="button">Load more</button>
                </div>
            `;
        }

        listEl.innerHTML = html;

        if (!this._listDelegated) {
            this._listDelegated = true;
            this.events.on(listEl, 'click', (e) => {
                // Load more
                const loadMoreBtn = e.target.closest('.notifications-panel__load-more');
                if (loadMoreBtn) {
                    e.stopPropagation();
                    this.loadMore();
                    return;
                }

                // Group header toggle
                const groupHeader = e.target.closest('.notifications-panel__group-header');
                if (groupHeader) {
                    e.stopPropagation();
                    const idx = groupHeader.dataset.groupIdx;
                    if (this._expandedGroups.has(idx)) {
                        this._expandedGroups.delete(idx);
                    } else {
                        this._expandedGroups.add(idx);
                    }
                    this._renderNotificationsList(listEl);
                    return;
                }

                // Individual notification click
                const item = e.target.closest('.notifications-panel__item');
                if (item) {
                    const notification = this.notifications.find(n => n.id === item.dataset.id);
                    if (notification) this.handleNotificationClick(notification);
                }
            });
        }
    }

    _renderGroup(group, groupIdx) {
        const isExpanded = this._expandedGroups.has(String(groupIdx));
        const first = group.items[0];
        const cfg = getTypeConfig(group.type);
        const unreadInGroup = group.items.filter(n => !n.is_read).length;

        const actorName = cfg.initials ? getActorName(first) : null;
        const iconHtml = actorName ? getInitials(actorName) : `<i class="fas ${cfg.icon}"></i>`;

        const label = getNotificationLabel(group.type, first.title);

        let html = `
            <div class="notifications-panel__group ${unreadInGroup ? 'notifications-panel__group--has-unread' : ''}">
                <div class="notifications-panel__group-header ${isExpanded ? 'notifications-panel__group-header--expanded' : ''}" data-group-idx="${groupIdx}">
                    <div class="notifications-panel__icon notifications-panel__icon--${cfg.cls}">
                        ${iconHtml}
                    </div>
                    <div class="notifications-panel__group-info">
                        <span class="notifications-panel__group-label">${escapeHtml(label)} (${group.items.length})</span>
                        <span class="notifications-panel__group-time">${getRelativeTime(first.created_at)}</span>
                    </div>
                    ${unreadInGroup ? `<div class="notifications-panel__dot"></div>` : ''}
                    <i class="fas fa-chevron-right notifications-panel__group-chevron ${isExpanded ? 'notifications-panel__group-chevron--expanded' : ''}"></i>
                </div>
        `;

        if (isExpanded) {
            html += `<div class="notifications-panel__group-items">`;
            for (const n of group.items) {
                html += this._renderNotification(n, true);
            }
            html += `</div>`;
        }

        html += `</div>`;
        return html;
    }

    _renderNotification(notification, isNested = false) {
        const type = notification.type;
        const cfg = getTypeConfig(type);

        // Use an avatar (initials) for person-centric types, otherwise the FA icon.
        const actorName = cfg.initials ? getActorName(notification) : null;
        const iconHtml = actorName ? getInitials(actorName) : `<i class="fas ${cfg.icon}"></i>`;

        const unreadCls = notification.is_read ? '' : 'notifications-panel__item--unread';
        const nestedCls = isNested ? 'notifications-panel__item--nested' : '';
        const { quote, context } = getNotificationDetail(notification);
        const hasLink = !!getNavigationUrl(notification);

        // Clean message: strip @mention markdown and newlines for display
        const cleanMessage = stripMentionMarkdown((notification.message || '').replace(/\n/g, ' '));

        return `
            <div class="notifications-panel__item ${unreadCls} ${nestedCls}" data-id="${notification.id}">
                ${!notification.is_read ? '<div class="notifications-panel__unread-bar"></div>' : ''}
                <div class="notifications-panel__icon notifications-panel__icon--${cfg.cls}">
                    ${iconHtml}
                </div>
                <div class="notifications-panel__item-content">
                    <div class="notifications-panel__item-title">${escapeHtml(stripMentionMarkdown(notification.title))}</div>
                    <div class="notifications-panel__item-message">${escapeHtml(cleanMessage)}</div>
                    ${quote ? `<div class="notifications-panel__item-quote">${escapeHtml(quote)}</div>` : ''}
                    ${context ? `<div class="notifications-panel__item-context">${escapeHtml(context)}</div>` : ''}
                </div>
                <div class="notifications-panel__item-meta">
                    <span class="notifications-panel__item-time" data-ts="${notification.created_at}">${getRelativeTime(notification.created_at)}</span>
                    ${!notification.is_read ? '<div class="notifications-panel__dot"></div>' : ''}
                    ${hasLink ? '<i class="fas fa-chevron-right notifications-panel__item-link"></i>' : ''}
                </div>
            </div>
        `;
    }

    // -----------------------------------------------------------------------
    // Navigation
    // -----------------------------------------------------------------------

    async handleNotificationClick(notification) {
        if (!notification.is_read) {
            const itemEl = this.container?.querySelector(`[data-id="${notification.id}"]`);
            if (itemEl) itemEl.setAttribute('aria-busy', 'true');

            try {
                const { success, error } = await markAsRead(notification.id);
                if (!success) throw error || new Error('Failed to mark as read');

                notification.is_read = true;
                this.refreshUnreadCount();

                // Update DOM immediately
                if (itemEl) {
                    itemEl.classList.remove('notifications-panel__item--unread');
                    const bar = itemEl.querySelector('.notifications-panel__unread-bar');
                    if (bar) bar.remove();
                    const dot = itemEl.querySelector('.notifications-panel__dot');
                    if (dot) dot.remove();
                }
            } catch (err) {
                console.error('[NotificationsPanel] Failed to mark as read:', err);
                showToast('Failed to mark as read', 'error');
            } finally {
                if (itemEl?.isConnected) itemEl.removeAttribute('aria-busy');
            }
        }

        const url = getNavigationUrl(notification);
        if (url) {
            this.close();
            window.location.hash = url;
        }
    }


    // -----------------------------------------------------------------------
    // View switching (Notifications / Reminders)
    // -----------------------------------------------------------------------

    _switchView(view) {
        this._activeView = view;

        // Update tab active states
        const tabs = this.container?.querySelectorAll('.notifications-panel__tab');
        tabs?.forEach(tab => {
            tab.classList.toggle('notifications-panel__tab--active', tab.dataset.tab === view);
        });

        const listEl = this.container?.querySelector('#notifications-list');
        const remindersEl = this.container?.querySelector('#notifications-reminders');
        const footerEl = this.container?.querySelector('#notifications-footer');

        // Toggle pre-rendered action button sets by data-view attribute
        const actionsEl = this.container?.querySelector('#notifications-header-actions');
        actionsEl?.querySelectorAll('[data-view]').forEach(el => {
            el.style.display = el.dataset.view === view ? '' : 'none';
        });

        const isNotifications = view === 'notifications';
        if (listEl) listEl.style.display = isNotifications ? '' : 'none';
        if (remindersEl) remindersEl.style.display = isNotifications ? 'none' : '';
        if (footerEl) footerEl.style.display = isNotifications ? '' : 'none';

        if (isNotifications) {
            this.loadNotifications();
            this._startTimestampUpdater();
        } else {
            this._stopTimestampUpdater();
            this._loadReminders();
        }
    }

    // -----------------------------------------------------------------------
    // Reminders
    // -----------------------------------------------------------------------

    async _loadReminders(force = false) {
        const remindersEl = this.container?.querySelector('#notifications-reminders');
        if (!remindersEl) return;

        // Skip fetch if already loaded and not forced (e.g. tab switch back)
        if (this._remindersLoaded && !force) {
            this._renderRemindersView(remindersEl);
            return;
        }

        remindersEl.innerHTML = `
            <div class="notifications-panel__loading">
                <i class="fas fa-spinner fa-spin"></i>
            </div>
        `;

        const { data, error } = await getMyReminders({ upcoming: true });

        if (error) {
            remindersEl.innerHTML = `
                <div class="notifications-panel__empty">
                    <i class="fas fa-exclamation-circle"></i>
                    <span class="notifications-panel__empty-title">Something went wrong</span>
                    <span class="notifications-panel__empty-subtitle">Failed to load reminders</span>
                </div>
            `;
            return;
        }

        this._reminders = data || [];
        this._remindersLoaded = true;
        this._renderRemindersView(remindersEl);
    }

    _renderRemindersView(remindersEl) {
        if (!this._reminders.length) {
            remindersEl.innerHTML = `
                <div class="notifications-panel__empty">
                    <i class="fas fa-clock"></i>
                    <span class="notifications-panel__empty-title">No upcoming reminders</span>
                    <span class="notifications-panel__empty-subtitle">Click <strong>+</strong> to set one</span>
                </div>
            `;
            return;
        }

        // Group reminders by status (time-aware: a passed moment is overdue)
        const now = new Date();
        const tomorrowStart = new Date();
        tomorrowStart.setHours(0, 0, 0, 0);
        tomorrowStart.setDate(tomorrowStart.getDate() + 1);
        const groups = { overdue: [], today: [], upcoming: [] };

        for (const r of this._reminders) {
            const at = new Date(r.remind_at);
            if (at < now) groups.overdue.push(r);
            else if (at < tomorrowStart) groups.today.push(r);
            else groups.upcoming.push(r);
        }

        const sections = [
            { key: 'overdue', label: 'Overdue', icon: 'fa-exclamation-circle' },
            { key: 'today', label: 'Today', icon: 'fa-clock' },
            { key: 'upcoming', label: 'Upcoming', icon: 'fa-calendar' }
        ];

        let html = '';
        for (const section of sections) {
            const items = groups[section.key];
            if (!items.length) continue;

            html += `<div class="notifications-panel__reminder-group">
                <div class="notifications-panel__reminder-group-label">
                    <i class="fas ${section.icon}"></i> ${section.label}
                </div>`;

            for (const r of items) {
                const dateStr = this._formatReminderDateTime(r.remind_at);
                const statusCls = section.key === 'overdue' ? ' notifications-panel__reminder--overdue'
                    : section.key === 'today' ? ' notifications-panel__reminder--today' : '';
                const creatorName = r.creator?.full_name || r.creator?.short_name || '';
                const contextHtml = r.context_name
                    ? `<span class="notifications-panel__reminder-context">${escapeHtml(r.context_name)}</span>` : '';

                html += `
                    <div class="notifications-panel__reminder${statusCls}" data-reminder-id="${r.id}">
                        <div class="notifications-panel__reminder-date">${dateStr}</div>
                        <div class="notifications-panel__reminder-body">
                            <div class="notifications-panel__reminder-title">${escapeHtml(r.title)}</div>
                            <div class="notifications-panel__reminder-meta">
                                ${creatorName ? `<span>by ${escapeHtml(creatorName)}</span>` : ''}
                                ${contextHtml}
                            </div>
                        </div>
                        <button class="notifications-panel__reminder-delete" data-reminder-id="${r.id}" title="Delete">
                            <i class="fas fa-trash-alt"></i>
                        </button>
                    </div>
                `;
            }
            html += '</div>';
        }

        remindersEl.innerHTML = html;

        // Attach delete handlers via delegation
        if (!this._remindersDelegated) {
            this._remindersDelegated = true;
            this.events.on(remindersEl, 'click', async (e) => {
                const deleteBtn = e.target.closest('.notifications-panel__reminder-delete');
                if (deleteBtn) {
                    e.stopPropagation();
                    const id = deleteBtn.dataset.reminderId;
                    if (!id) return;
                    deleteBtn.disabled = true;
                    deleteBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
                    const { success } = await deleteReminder(id);
                    if (success) {
                        this._reminders = this._reminders.filter(r => r.id !== id);
                        this._renderRemindersView(remindersEl);
                        this._refreshRemindersCount();
                        const { showToast } = await import('shared/toast/toast.js');
                        showToast('Reminder deleted', 'success');
                    } else {
                        deleteBtn.disabled = false;
                        deleteBtn.innerHTML = '<i class="fas fa-trash-alt"></i>';
                        const { showToast } = await import('shared/toast/toast.js');
                        showToast('Failed to delete', 'error');
                    }
                    return;
                }

                // Click on reminder item navigates to context
                const item = e.target.closest('.notifications-panel__reminder');
                if (item) {
                    const id = item.dataset.reminderId;
                    const reminder = this._reminders.find(r => r.id === id);
                    if (reminder?.context_type === 'task' && reminder?.context_id) {
                        this.close();
                        window.location.hash = `#/task?id=${reminder.context_id}`;
                    }
                }
            });
        }
    }

    _formatReminderDateTime(iso) {
        if (!iso) return '';
        const d = new Date(iso);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${d.getDate()} ${months[d.getMonth()]}<br>${hh}:${mm}`;
    }

    async _refreshRemindersCount() {
        this._remindersCount = await getRemindersCount();
        const badge = this.container?.querySelector('#reminders-tab-badge');
        if (badge) {
            badge.textContent = this._remindersCount;
            badge.classList.toggle('notifications-panel__tab-badge--hidden', this._remindersCount === 0);
        }
    }

    // -----------------------------------------------------------------------
    // Mark all read
    // -----------------------------------------------------------------------

    async handleMarkAllRead() {
        const { success, error } = await markAllAsRead();
        if (!success) throw error || new Error('Failed to mark all as read');
        this.notifications.forEach(n => n.is_read = true);
        await this.refreshUnreadCount();
        // Re-render in place to clear unread styles
        const listEl = this.container?.querySelector('#notifications-list');
        if (listEl && this.notifications.length) {
            this._renderNotificationsList(listEl);
        }
    }

    // -----------------------------------------------------------------------
    // Badge / Count
    // -----------------------------------------------------------------------

    async refreshUnreadCount() {
        this.unreadCount = await getUnreadCount();
        this.updateBadge();
    }

    updateBadge() {
        // null until the first count resolves, so a fresh session can tell
        // "here is your standing count" apart from "one just landed".
        const previous = this._renderedCount;
        this._renderedCount = this.unreadCount;
        const isFirstResolve = previous === null;
        const grew = !isFirstResolve && this.unreadCount > previous;

        this.bellEl?.classList.toggle('notifications-bell--has-unread', this.unreadCount > 0);

        const badge = this.container?.querySelector('#notifications-badge');
        if (badge) {
            badge.textContent = this.unreadCount > 9 ? '9+' : this.unreadCount;
            badge.classList.toggle('notifications-bell__badge--hidden', this.unreadCount === 0);

            if (this.unreadCount > 0 && (grew || isFirstResolve)) {
                this._playBadgeArrival(badge, grew);
            }
        }

        const tabBadge = this.container?.querySelector('#notifications-tab-badge');
        if (tabBadge) {
            tabBadge.textContent = this.unreadCount > 9 ? '9+' : this.unreadCount;
            tabBadge.classList.toggle('notifications-panel__tab-badge--hidden', this.unreadCount === 0);
        }
    }

    /**
     * One-shot arrival flourish on the count pill. `swing` also nudges the bell
     * itself - reserved for a notification that genuinely just landed, so the
     * standing count on login stays quiet.
     *
     * Removing the class and forcing a reflow restarts the animation when two
     * notifications land back to back.
     */
    _playBadgeArrival(badge, swing) {
        badge.classList.remove('notifications-bell__badge--arrived');
        void badge.offsetWidth;
        badge.classList.add('notifications-bell__badge--arrived');

        if (swing && this.bellEl) {
            this.bellEl.classList.remove('notifications-bell--arrived');
            void this.bellEl.offsetWidth;
            this.bellEl.classList.add('notifications-bell--arrived');
        }

        // Keyed, so a rapid second arrival replaces the pending cleanup rather
        // than stripping the class mid-replay.
        this.events.bindTimeout('badge:arrival', () => {
            badge.classList.remove('notifications-bell__badge--arrived');
            this.bellEl?.classList.remove('notifications-bell--arrived');
        }, 950);
    }

    // -----------------------------------------------------------------------
    // Timestamp updater (runs every 30s while panel is open)
    // -----------------------------------------------------------------------

    _startTimestampUpdater() {
        this._stopTimestampUpdater();
        this._timestampInterval = this.events.setInterval(() => {
            const timeEls = this.container?.querySelectorAll('.notifications-panel__item-time[data-ts]');
            if (timeEls) {
                timeEls.forEach(el => {
                    el.textContent = getRelativeTime(el.dataset.ts);
                });
            }
        }, 30_000);
    }

    _stopTimestampUpdater() {
        if (this._timestampInterval) {
            this.events.clearInterval(this._timestampInterval);
            this._timestampInterval = null;
        }
    }

    // -----------------------------------------------------------------------
    // Cleanup
    // -----------------------------------------------------------------------

    destroy() {
        // Releases every DOM listener, window event, timer, and interval the
        // panel registered. Any future open() will repopulate through
        // attachEventListeners(), so the panel is re-initializable.
        this.events.dispose();
        this.events = new EventManager('NotificationsPanel');

        this._timestampInterval = null;
        this._autoReadTimer = null;
        this.container = null;
        this.panelEl = null;
        this.bellEl = null;
        this.notifications = [];
        this.unreadCount = 0;
        this._renderedCount = null;
        this.isOpen = false;
        this._listDelegated = false;
        this._remindersDelegated = false;
        this._expandedGroups.clear();
        this._reminders = [];
        this._remindersCount = 0;
        this._activeView = 'notifications';
    }
}

export const notificationsPanel = new NotificationsPanel();
